'use client';

import { useTranslation } from 'react-i18next';

import { LinkStyle } from '@sola/ark-core/atoms/link-style/link-style.tsx';
import { ItemCardFooter } from '@sola/ark-core/molecules/item-card/item-card.tsx';
import type {
  GraphQLCommitment,
  GraphQLSupporterPaymentMethod,
} from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { GraphQLCardExpiryStatus } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { useSupporterQuery } from '@sola/supporter-sdk/hooks/base.ts';
import {
  commitmentsQuery,
  paymentMethodsQuery,
} from '@sola/supporter-sdk/queries.ts';

import { PaymentMethodRemoveDialog } from '../../organisms/payment-method-remove-dialog/payment-method-remove-dialog.tsx';
import { PaymentMethodUpdateSheet } from '../../organisms/payment-method-update-sheet/payment-method-update-sheet.tsx';

export const useComponentData = (
  currentPaymentMethod: GraphQLSupporterPaymentMethod,
) => {
  const { data: paymentMethodData } = useSupporterQuery([
    paymentMethodsQuery,
    commitmentsQuery,
  ]);

  const [paymentMethods] = paymentMethodData;

  return {
    alternatePaymentMethods: paymentMethods
      ? paymentMethods.filter(
          (pm) =>
            pm.id !== currentPaymentMethod.id &&
            pm.status !== 'Expired' &&
            pm.creditCard?.expiryStatus !== GraphQLCardExpiryStatus.Expired,
        )
      : [],
  };
};

export type PaymentMethodCardFooterProps = {
  paymentMethod: GraphQLSupporterPaymentMethod;
  recurringCommitments?: GraphQLCommitment[];
  allCommitments?: GraphQLCommitment[];
  onRemoveSuccess?: () => void;
  onRemoveError?: () => void;
  onCommitmentsUpdated?: (updatedCommitments: GraphQLCommitment[]) => void;
};

export const PaymentMethodCardFooter: React.FC<
  PaymentMethodCardFooterProps
> = ({
  paymentMethod,
  recurringCommitments = [],
  allCommitments = [],
  onCommitmentsUpdated,
}) => {
  const { t } = useTranslation('my');

  const { alternatePaymentMethods } = useComponentData(paymentMethod);

  const isShowingRemoveBtn =
    !recurringCommitments.length || !!alternatePaymentMethods?.length;

  return (
    <ItemCardFooter justify="end">
      <div className="flex justify-end items-center gap-comfy-sm md:gap-comfy-md">
        <div className="flex gap-comfy-md ml-auto">
          {isShowingRemoveBtn && (
            <PaymentMethodRemoveDialog
              openTrigger={
                <LinkStyle noUnderline noPadding size="lg" purpose="negative">
                  <button type="button">{t('payments.removeMethod')}</button>
                </LinkStyle>
              }
              selectedPaymentMethod={paymentMethod}
              recurringCommitments={recurringCommitments}
              alternatePaymentMethods={alternatePaymentMethods}
            />
          )}

          <PaymentMethodUpdateSheet
            openTrigger={
              <LinkStyle noUnderline noPadding size="lg">
                <button type="button">{t('payments.editMethod')}</button>
              </LinkStyle>
            }
            paymentMethod={paymentMethod}
            commitments={allCommitments}
            onCommitmentsUpdated={onCommitmentsUpdated}
          />
        </div>
      </div>
    </ItemCardFooter>
  );
};
